import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../utils/api';
import { Professor, PaginatedResponse, SearchFilters } from '../../types';

// Types
interface ProfessorState {
  professors: Professor[];
  recommendedProfessors: Professor[];
  currentProfessor: Professor | null;
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    pages: number;
    page: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
  };
  filters: SearchFilters;
}

// Async thunks
export const fetchProfessors = createAsyncThunk(
  'professors/fetchProfessors',
  async (params: { page?: number; per_page?: number; filters?: SearchFilters }, { rejectWithValue }) => {
    try {
      const { page = 1, per_page = 20, filters = {} } = params;
      
      // Build query parameters
      const queryParams = new URLSearchParams({
        page: page.toString(),
        per_page: per_page.toString()
      });
      
      // Add filters to query parameters
      if (filters.university_id) {
        queryParams.append('university_id', filters.university_id.toString());
      }
      
      if (filters.department_id) {
        queryParams.append('department_id', filters.department_id.toString());
      }
      
      if (filters.research_area_id) {
        queryParams.append('research_area_id', filters.research_area_id.toString());
      }
      
      if (filters.query) {
        queryParams.append('q', filters.query);
      }
      
      const response = await api.get<PaginatedResponse<Professor>>(`/professors?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch professors.'
      );
    }
  }
);

export const fetchProfessorById = createAsyncThunk(
  'professors/fetchProfessorById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.get<{ professor: Professor }>(`/professors/${id}`);
      return response.data.professor;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch professor details.'
      );
    }
  }
);

export const fetchRecommendedProfessors = createAsyncThunk(
  'professors/fetchRecommendedProfessors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<{ professors: Professor[] }>('/professors/recommend');
      return response.data.professors;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch recommended professors.'
      );
    }
  }
);

// Initial state
const initialState: ProfessorState = {
  professors: [],
  recommendedProfessors: [],
  currentProfessor: null,
  loading: false,
  error: null,
  pagination: {
    total: 0,
    pages: 0,
    page: 1,
    per_page: 20,
    has_next: false,
    has_prev: false
  },
  filters: {}
};

// Slice
const professorSlice = createSlice({
  name: 'professors',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<SearchFilters>) {
      state.filters = action.payload;
    },
    clearFilters(state) {
      state.filters = {};
    },
    clearError(state) {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch professors
      .addCase(fetchProfessors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfessors.fulfilled, (state, action: PayloadAction<PaginatedResponse<Professor>>) => {
        state.loading = false;
        state.professors = action.payload.items;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchProfessors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch professor by ID
      .addCase(fetchProfessorById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfessorById.fulfilled, (state, action: PayloadAction<Professor>) => {
        state.loading = false;
        state.currentProfessor = action.payload;
      })
      .addCase(fetchProfessorById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch recommended professors
      .addCase(fetchRecommendedProfessors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecommendedProfessors.fulfilled, (state, action: PayloadAction<Professor[]>) => {
        state.loading = false;
        state.recommendedProfessors = action.payload;
      })
      .addCase(fetchRecommendedProfessors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { setFilters, clearFilters, clearError } = professorSlice.actions;

export default professorSlice.reducer;
